import React, { useMemo, useState } from 'react';
import {
    Box, Card, CardContent, Chip, LinearProgress,
    Skeleton, Stack, Tooltip, Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import { useForm } from 'react-hook-form';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PageTitle from '../../components/PageTitle';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { useGetNdrScopeSummaryQuery } from '../../redux/interventionApiSlice';
import { FormProvider, RHFSearchableSelectField } from '../../common/hook-form';
import { MerchantFilterCached } from '../../common/ProshipFormComponents';
import {
    FilterCard, FilterCardContent, FilterRow, FilterField, FieldLabel,
    FilterActions, ApplyButton, ResetButton,
} from '../../common/FilterBar';

// WhatsApp NDR / NDR Calling are EXACT merchant-panel pill parity:
// WhatsApp = whatsappAutomatedNdr, Calling = futworksNdrConfig.pushLead ONLY
// (human tele-calling — the futworkAI channel is deliberately skipped here,
// same as the merchant panel). "Is Prozo managing this merchant's NDR" is
// answered directly by ndrManagedByProzo — the field built for that exact
// question — NOT inferred from the toggles (a merchant can have every toggle
// off yet still be Prozo-managed, or vice versa).
function scopeLabel(flags) {
    if (!flags?.managedByProzo) return 'Merchant self-managed';
    if (flags?.calling && flags?.whatsapp) return 'Prozo — Calling + WhatsApp';
    if (flags?.calling) return 'Prozo — Calling';
    if (flags?.whatsapp) return 'Prozo — WhatsApp';
    return 'Prozo-managed';
}

const SCOPE_FILTERS = [
    { value: 'ALL', label: 'All scopes' },
    { value: 'PROZO', label: 'Prozo-managed (any)' },
    { value: 'CALLING', label: 'Calling active' },
    { value: 'WHATSAPP', label: 'WhatsApp enabled' },
    { value: 'SELF', label: 'Merchant self-managed' },
];

// Precise, two-line explanation per KPI: what it means, then exactly how it's
// computed — so a viewer never has to guess what a number is counting.
const KPI_INFO = {
    total: {
        what: 'Total merchants in scope.',
        how: 'Active, KYC-approved merchants, excluding test accounts.',
    },
    prozo: {
        what: 'Prozo is responsible for this merchant’s NDR resolution.',
        how: 'Count of merchants with ndrManagedByProzo = true on their config.',
    },
    calling: {
        what: 'NDR calling automation is running for the merchant.',
        how: 'Count of merchants with futworksNdrConfig.pushLead = true (human tele-calling). Same field the merchant panel checks.',
    },
    whatsapp: {
        what: 'Automated WhatsApp NDR follow-up is switched on.',
        how: 'Count of merchants with whatsappAutomatedNdr = true on their config.',
    },
    self: {
        what: 'Merchant handles their own NDR follow-up.',
        how: 'Count of merchants with ndrManagedByProzo = false.',
    },
};

// Custom-styled tooltip — small, dark, soft-shadowed pop-over so the note
// reads as a native part of the design system rather than a bolted-on hint.
const InfoTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow placement="top" classes={{ popper: className }} />
))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#1A2027',
        color: '#E9EDF1',
        fontSize: 11.5,
        lineHeight: 1.55,
        padding: '10px 13px',
        borderRadius: 10,
        maxWidth: 230,
        boxShadow: '0 12px 28px rgba(0,0,0,0.28)',
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: '#1A2027',
    },
}));

function KpiTile({ icon, label, value, color, info }) {
    return (
        <Card variant="outlined" sx={{ flex: 1, minWidth: 160 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{
                    width: 38, height: 38, borderRadius: '10px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    bgcolor: `${color}18`, color,
                }}>
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.4}>
                        <Typography variant="caption" sx={{
                            color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: 0.3, whiteSpace: 'nowrap',
                        }}>
                            {label}
                        </Typography>
                        {info && (
                            <InfoTooltip
                                title={(
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: 11.5, mb: 0.4, color: '#fff' }}>
                                            {info.what}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: 'rgba(233,237,241,0.72)' }}>
                                            {info.how}
                                        </Typography>
                                    </Box>
                                )}
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        fontSize: 12.5, color: 'text.disabled', opacity: 0.55, cursor: 'help',
                                        transition: 'opacity 0.15s ease, color 0.15s ease',
                                        '&:hover': { opacity: 1, color },
                                    }}
                                />
                            </InfoTooltip>
                        )}
                    </Stack>
                    <Typography component="div" sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.2 }}>{value}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function NdrScope() {
    // ONE cached summary (2 bulk API calls total: merchant base + config/search
    // projection) — no per-merchant requests. Cached 30 min → instant revisits.
    const { data: summary, isFetching, isError, refetch } = useGetNdrScopeSummaryQuery();

    // Standard filter-bar form (same look/flow as FilterBar): values take effect
    // on FILTER, clear on RESET.
    const methods = useForm({ defaultValues: { merchant: '', scope: 'ALL' } });
    const { handleSubmit, reset } = methods;
    const [applied, setApplied] = useState({ merchant: '', scope: 'ALL' });
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(15);

    const rows = useMemo(() => {
        if (!summary) return [];
        let list = summary.merchants;
        if (applied.merchant) list = list.filter((m) => m.id === applied.merchant);
        return list.map((m) => {
            const flags = summary.configs[m.id];
            return {
                id: m.id,
                name: m.name || '—',
                whatsapp: !!flags?.whatsapp,
                calling: !!flags?.calling,
                prozoManaged: !!flags?.managedByProzo,
                scope: scopeLabel(flags),
            };
        });
    }, [summary, applied.merchant]);

    const filtered = useMemo(() => rows.filter((r) => {
        if (applied.scope === 'PROZO') return r.prozoManaged;
        if (applied.scope === 'CALLING') return r.calling;
        if (applied.scope === 'WHATSAPP') return r.whatsapp;
        if (applied.scope === 'SELF') return !r.prozoManaged;
        return true;
    }), [rows, applied.scope]);

    const paged = filtered.slice(page * sizePerPage, (page + 1) * sizePerPage);

    const kpis = useMemo(() => {
        if (!summary) return null;
        const flagsList = summary.merchants.map((m) => summary.configs[m.id]);
        return {
            total: summary.merchants.length,
            prozo: flagsList.filter((f) => f?.managedByProzo).length,
            calling: flagsList.filter((f) => f?.calling).length,
            whatsapp: flagsList.filter((f) => f?.whatsapp).length,
            self: flagsList.filter((f) => !f?.managedByProzo).length,
        };
    }, [summary]);

    const kpiValue = (v) => (kpis ? v : isError ? '—' : <Skeleton width={36} />);

    const columns = [
        {
            field: 'name', headerName: 'Merchant', flex: 1.4, minWidth: 220, hideSortIcons: true,
            renderCell: ({ row }) => (
                <Box sx={{ py: 0.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{row.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.id}</Typography>
                </Box>
            ),
        },
        {
            field: 'whatsapp', headerName: 'WhatsApp NDR', flex: 0.8, minWidth: 140, hideSortIcons: true,
            renderCell: ({ row }) => (
                <Chip
                    size="small" variant="outlined" icon={<WhatsAppIcon sx={{ fontSize: 14 }} />}
                    label={row.whatsapp ? 'Enabled' : 'Disabled'}
                    color={row.whatsapp ? 'success' : 'default'}
                    sx={{ fontWeight: 600, fontSize: 11 }}
                />
            ),
        },
        {
            field: 'calling', headerName: 'NDR Calling', flex: 0.8, minWidth: 140, hideSortIcons: true,
            renderCell: ({ row }) => (
                <Chip
                    size="small" variant="outlined" icon={<PhoneInTalkIcon sx={{ fontSize: 14 }} />}
                    label={row.calling ? 'Active' : 'Inactive'}
                    color={row.calling ? 'success' : 'default'}
                    sx={{ fontWeight: 600, fontSize: 11 }}
                />
            ),
        },
        {
            field: 'scope', headerName: 'NDR Scope', flex: 1.1, minWidth: 200, hideSortIcons: true,
            renderCell: ({ row }) => (
                <Chip
                    size="small"
                    label={row.scope}
                    color={row.prozoManaged ? 'primary' : 'warning'}
                    variant={row.prozoManaged ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 700, fontSize: 11 }}
                />
            ),
        },
    ];

    return (
        <>
            <PageTitle>NDR Scope — Merchant Coverage</PageTitle>

            {/* KPI tiles — fill in one go from the single cached summary */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                <KpiTile icon={<GroupsOutlinedIcon />} label="Merchants" value={kpiValue(kpis?.total)} color="#1976d2" info={KPI_INFO.total} />
                <KpiTile icon={<SupportAgentOutlinedIcon />} label="Prozo-managed" value={kpiValue(kpis?.prozo)} color="#2e7d32" info={KPI_INFO.prozo} />
                <KpiTile icon={<PhoneInTalkIcon />} label="Calling active" value={kpiValue(kpis?.calling)} color="#0288d1" info={KPI_INFO.calling} />
                <KpiTile icon={<WhatsAppIcon />} label="WhatsApp enabled" value={kpiValue(kpis?.whatsapp)} color="#25D366" info={KPI_INFO.whatsapp} />
                <KpiTile icon={<PersonOffOutlinedIcon />} label="Self-managed" value={kpiValue(kpis?.self)} color="#ed6c02" info={KPI_INFO.self} />
            </Box>

            {isFetching && !summary && <LinearProgress sx={{ mb: 2 }} />}
            {isError && !summary && (
                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mb: 2 }}>
                    Couldn't load NDR scope data.{' '}
                    <Typography component="span" variant="caption" onClick={() => refetch()}
                        sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}>
                        Retry
                    </Typography>
                </Typography>
            )}

            {/* Filters — standard FilterBar look, composed from its exported pieces */}
            <FilterCard>
                <FilterCardContent>
                    <FormProvider
                        methods={methods}
                        onSubmit={handleSubmit((data) => { setApplied({ merchant: data.merchant || '', scope: data.scope || 'ALL' }); setPage(0); })}
                    >
                        <FilterRow>
                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth placeholder="Select Merchant" />
                            </FilterField>
                            <FilterField>
                                <FieldLabel>NDR Scope</FieldLabel>
                                <RHFSearchableSelectField
                                    fullWidth
                                    name="scope"
                                    label="Select Scope"
                                    placeholder="Select Scope"
                                    options={SCOPE_FILTERS}
                                />
                            </FilterField>
                            <FilterActions>
                                <ApplyButton type="submit" variant="contained" size="small">
                                    Filter
                                </ApplyButton>
                                <ResetButton
                                    type="button" variant="contained" size="small"
                                    onClick={() => { reset({ merchant: '', scope: 'ALL' }); setApplied({ merchant: '', scope: 'ALL' }); setPage(0); }}
                                >
                                    Reset
                                </ResetButton>
                            </FilterActions>
                        </FilterRow>
                    </FormProvider>
                </FilterCardContent>
            </FilterCard>

            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                {filtered.length} of {rows.length} merchants
            </Typography>

            <ProzoDataGrid
                columns={columns}
                rows={paged}
                rowCount={filtered.length}
                page={page}
                setPage={setPage}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                pagination
                autoHeight
                rowHeight={56}
                getRowId={(row) => row.id}
                loading={isFetching && !summary}
            />
        </>
    );
}
